import { ReactNode, useState, useCallback, useMemo } from 'react'
import { NotificationProvider } from '@contexts/NotificationContext'
import { Toast, ToastType } from './feedback/Toast'
import ErrorBoundary from './feedback/ErrorBoundary/ErrorBoundary'

interface RootProviderProps {
  children: ReactNode
}

export default function RootProvider({ children }: RootProviderProps) {
  const [toasts, setToasts] = useState<Toast[]>([])

  const showToast = useCallback((
    type: ToastType,
    title: string,
    options?: {
      message?: string
      duration?: number
      dismissible?: boolean
      progress?: number
    }
  ): string => {
    const id = `toast-${Date.now()}-${Math.random()}`

    const newToast: Toast = {
      id,
      type,
      title,
      message: options?.message,
      progress: options?.progress,
      duration: options?.duration ?? (type === 'error' ? 5000 : 3000),
      dismissible: options?.dismissible !== false
    }

    setToasts((prev) => [...prev, newToast])
    return id
  }, [])

  const updateToast = useCallback((id: string, updates: Partial<Toast>) => {
    setToasts((prev) =>
      prev.map((toast) =>
        toast.id === id ? { ...toast, ...updates } : toast
      )
    )
  }, [])

  const dismissToast = useCallback((id: string) => {
    setToasts((prev) => prev.filter((toast) => toast.id !== id))
  }, [])

  const clearAll = useCallback(() => {
    setToasts([])
  }, [])

  // Memoize the context value to prevent unnecessary re-renders of consumers
  // Only recreate when the actual values change
  const notifications = useMemo(() => ({
    toasts,
    showToast,
    updateToast,
    dismissToast,
    clearAll
  }), [toasts, showToast, updateToast, dismissToast, clearAll])

  return (
    <ErrorBoundary>
      <NotificationProvider value={notifications}>
        {children}
      </NotificationProvider>
    </ErrorBoundary>
  )
}
